// app/javascript/dashboard/routes/dashboard/bulkmessage/utils/useRecipients.js
import { ref, computed, watch } from 'vue';

export default function useRecipients(props, emit) {
  const searchQuery = ref('');
  const sortBy = ref({ id: 'name', desc: false });
  const selectedRows = computed({
    get: () => props.selectedIds,
    set: value => emit('update:selectedIds', value),
  });

  const pagination = ref({
    pageIndex: 0,
    pageSize: 10,
  });

  // Filter recipients based on search
  const filteredRecipients = computed(() => {
    const query = searchQuery.value.toLowerCase();
    if (!query) return props.recipients;

    return props.recipients.filter(recipient => {
      // Search in standard fields
      if (recipient.name?.toLowerCase().includes(query)) return true;
      if (recipient.phone?.toLowerCase().includes(query)) return true;
      if (recipient.email?.toLowerCase().includes(query)) return true;

      // Search in custom fields
      if (recipient.customFields) {
        for (const fieldValue of Object.values(recipient.customFields)) {
          if (fieldValue && String(fieldValue).toLowerCase().includes(query)) {
            return true;
          }
        }
      }

      return false;
    });
  });

  // Apply sorting
  const sortedRecipients = computed(() => {
    return [...filteredRecipients.value].sort((a, b) => {
      const aValue = a[sortBy.value.id];
      const bValue = b[sortBy.value.id];

      if (sortBy.value.desc) {
        return aValue > bValue ? -1 : 1;
      }
      return aValue > bValue ? 1 : -1;
    });
  });

  // Add selection status to each recipient
  const recipientsForDisplayWithSelection = computed(() => {
    return sortedRecipients.value.map(recipient => ({
      ...recipient,
      selected: selectedRows.value.includes(recipient.id),
    }));
  });

  // Get only the page of data we need to display
  const pagedRecipients = computed(() => {
    const { pageIndex, pageSize } = pagination.value;
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return recipientsForDisplayWithSelection.value.slice(start, end);
  });

  // Check if all results are selected
  const allSelected = computed(() => {
    return (
      filteredRecipients.value.length > 0 &&
      selectedRows.value.length === filteredRecipients.value.length
    );
  });

  // Handle page changes
  const handlePageChange = newPage => {
    // ContactsPagination uses 1-based page numbers, but internal state uses 0-based
    const pageIndex = newPage - 1;
    // Ensure page index is within bounds
    const maxPageIndex = Math.max(0, Math.ceil(filteredRecipients.value.length / pagination.value.pageSize) - 1);
    pagination.value.pageIndex = Math.min(Math.max(0, pageIndex), maxPageIndex);
  };

  // Handle page size changes
  const handlePageSizeChange = newSize => {
    pagination.value.pageSize = Number(newSize);
    pagination.value.pageIndex = 0; // Reset to first page when changing page size
  };

  // Selection methods
  const toggleAllSelection = value => {
    if (value) {
      selectedRows.value = filteredRecipients.value.map(r => r.id);
    } else {
      selectedRows.value = [];
    }
  };

  const toggleRowSelection = id => {
    const index = selectedRows.value.indexOf(id);
    if (index === -1) {
      selectedRows.value = [...selectedRows.value, id];
    } else {
      selectedRows.value = selectedRows.value.filter(rowId => rowId !== id);
    }
  };

  // Delete selected contacts
  const deleteSelectedRecipients = () => {
    if (selectedRows.value.length > 0) {
      emit('delete-selected', selectedRows.value);
      selectedRows.value = []; // Clear selection after deleting
    }
  };

  // Update methods
  const updateName = (id, value) => {
    emit('update-name', id, value);
  };

  const updatePhone = (id, value, validateFn) => {
    if (validateFn) {
      const validation = validateFn(value);
      if (validation.valid) {
        emit('update-phone', id, validation.formatted);
      }
    } else {
      emit('update-phone', id, value);
    }
  };

  const updateInbox = (id, value) => {
    console.log('[useRecipients.updateInbox] Single inbox update:', {
      timestamp: new Date().toISOString(),
      recipientId: id,
      newInboxId: Number(value)
    });
    emit('update-inbox', id, Number(value));
  };

  // Reset pagination to first page
  const resetPagination = () => {
    pagination.value.pageIndex = 0;
  };

  // Watch for changes that require pagination reset
  watch(
    () => filteredRecipients.value.length,
    (newLength, oldLength) => {
      // Calculate max valid page index
      const maxPageIndex = Math.max(0, Math.ceil(newLength / pagination.value.pageSize) - 1);

      // If current page is beyond the valid range, reset to last valid page or 0
      if (pagination.value.pageIndex > maxPageIndex) {
        pagination.value.pageIndex = maxPageIndex;
      }

      // If we went from 0 to some recipients (import), go to first page
      if (oldLength === 0 && newLength > 0) {
        pagination.value.pageIndex = 0;
      }

      // Check if current page is now empty after deletion
      const currentPageStart = pagination.value.pageIndex * pagination.value.pageSize;
      const itemsOnCurrentPage = newLength > currentPageStart ?
        Math.min(pagination.value.pageSize, newLength - currentPageStart) : 0;

      // If current page has no items and we're not on the first page, go to previous page
      if (itemsOnCurrentPage === 0 && pagination.value.pageIndex > 0 && newLength > 0) {
        pagination.value.pageIndex = Math.max(0, pagination.value.pageIndex - 1);
      }
    }
  );

  // Reset to first page when search query changes
  watch(searchQuery, () => {
    pagination.value.pageIndex = 0;
  });

  return {
    searchQuery,
    sortBy,
    selectedRows,
    pagination,
    filteredRecipients,
    sortedRecipients,
    recipientsForDisplayWithSelection,
    pagedRecipients,
    allSelected,
    handlePageChange,
    handlePageSizeChange,
    toggleAllSelection,
    toggleRowSelection,
    deleteSelectedRecipients,
    updateName,
    updatePhone,
    updateInbox,
    resetPagination,
  };
}
